import { useCallback, useEffect, useState } from 'react';
import { api } from '@halo/web-lib';
import { Info, Loader2, RefreshCw, ToggleRight } from 'lucide-react';

/* ─── Types ───────────────────────────────────── */

type FlagValue =
  | { kind: 'bool'; value: boolean }
  | { kind: 'number'; value: number };

interface RawFlag {
  key: string;
  value: boolean | number;
  source: string;
}

interface FeatureFlag {
  key: string;
  value: FlagValue;
  source: string;
}

interface FlagsServiceGroup {
  service: string;
  flags: FeatureFlag[];
  note: string;
}

interface RawFlagsResponse {
  service: string;
  flags: RawFlag[];
  note: string;
}

function toFlagValue(value: boolean | number): FlagValue {
  if (typeof value === 'boolean') {
    return { kind: 'bool', value };
  }
  return { kind: 'number', value };
}

function normalizeResponse(raw: RawFlagsResponse): FlagsServiceGroup {
  return {
    service: raw.service,
    note: raw.note,
    flags: raw.flags.map((f) => ({
      key: f.key,
      source: f.source,
      value: toFlagValue(f.value),
    })),
  };
}

/* ─── Value Cell ──────────────────────────────── */

function ValueCell({ value }: { value: FlagValue }) {
  if (value.kind === 'bool') {
    if (value.value) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
          ON
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
        <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
        OFF
      </span>
    );
  }
  return (
    <span className="font-mono text-xs text-gray-700 dark:text-gray-200">
      {value.value}
    </span>
  );
}

/* ─── Main Component ──────────────────────────── */

export function FeatureFlagsPage() {
  const [group, setGroup] = useState<FlagsServiceGroup | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  const fetchFlags = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/ingestion/admin/feature-flags');
      if (res.success && res.data) {
        setGroup(normalizeResponse(res.data as RawFlagsResponse));
        setLastRefresh(Date.now());
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFlags();
    const interval = setInterval(fetchFlags, 30_000);
    return () => clearInterval(interval);
  }, [fetchFlags]);

  const [refreshCountdown, setRefreshCountdown] = useState(30);
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshCountdown(Math.max(0, 30 - Math.floor((Date.now() - lastRefresh) / 1000)));
    }, 1000);
    return () => clearInterval(interval);
  }, [lastRefresh]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64" />
        <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-xl" />
      </div>
    );
  }

  const flags = group?.flags ?? [];
  const service = group?.service ?? '';

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ToggleRight className="w-5 h-5 text-gray-700 dark:text-gray-200" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Feature Flags</h2>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              Runtime flag visibility across ingestion services.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            {refreshCountdown}s
          </span>
          <button
            onClick={fetchFlags}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </button>
        </div>
      </div>

      {/* Read-only banner */}
      <div className="mb-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/60 text-amber-800 dark:text-amber-300 text-xs">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>Flags are read-only. Toggle via Cloud Run env vars + redeploy.</span>
      </div>

      {flags.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-10 flex flex-col items-center justify-center text-center">
          <ToggleRight className="w-10 h-10 text-gray-400 mb-2" />
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">No feature flags reported.</div>
          {group?.note && (
            <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">{group.note}</div>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-950 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="text-left px-4 py-2 font-medium">Service</th>
                <th className="text-left px-4 py-2 font-medium">Key</th>
                <th className="text-left px-4 py-2 font-medium">Value</th>
                <th className="text-left px-4 py-2 font-medium">Source</th>
              </tr>
            </thead>
            <tbody>
              {flags.map((f) => (
                <tr
                  key={`${service}-${f.key}`}
                  className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50/50 dark:hover:bg-gray-950/50"
                >
                  <td className="px-4 py-2.5 text-gray-900 dark:text-gray-100 font-medium">{service}</td>
                  <td className="px-4 py-2.5">
                    <span className="font-mono text-xs text-gray-700 dark:text-gray-200">{f.key}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <ValueCell value={f.value} />
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-600 dark:text-gray-400">{f.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
